import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './services/userKeyService';

// Index.tsx loaded

// Show the "new version available" banner (shared by SW updatefound and client-side version check)
let updateBannerShown = false;
function showUpdateBanner(registration: ServiceWorkerRegistration) {
  if (updateBannerShown) return;
  updateBannerShown = true;

  const updateBanner = document.createElement('div');
  updateBanner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #4CAF50;
    color: white;
    padding: 16px;
    text-align: center;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    font-family: Arial, sans-serif;
  `;

  const reloadButton = document.createElement('button');
  reloadButton.textContent = 'Reload Now';
  reloadButton.style.cssText = `
    margin-left: 20px;
    background: white;
    color: #4CAF50;
    border: none;
    padding: 8px 20px;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
  `;
  reloadButton.addEventListener('click', () => {
    reloadButton.textContent = 'Updating...';
    reloadButton.disabled = true;
    reloadButton.style.opacity = '0.6';
    reloadButton.style.cursor = 'not-allowed';

    // Standard pattern: tell waiting SW to activate, then reload in THIS tab when it takes control. No new tab.
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          // Avoid reload loop when DevTools "Update on reload" is checked (it forces immediate activation).
          const lastReload = sessionStorage.getItem('rc_sw_reload');
          const now = Date.now();
          if (lastReload && now - parseInt(lastReload, 10) < 3000) return;
          sessionStorage.setItem('rc_sw_reload', String(now));
          window.location.reload();
        },
        { once: true }
      );
    } else {
      // No waiting worker: unregister then reload in this same tab (no new tab).
      registration.unregister().then(
        () => setTimeout(() => window.location.reload(), 300),
        () => window.location.reload()
      );
    }
  });

  updateBanner.innerHTML = '<strong>🎉 A new version is available!</strong>';
  updateBanner.appendChild(reloadButton);
  const hint = document.createElement('div');
  hint.style.cssText = 'font-size: 12px; margin-top: 8px; opacity: 0.9;';
  hint.textContent = 'If Reload Now opens a new tab or you still see the old version, do a hard refresh: Mac Cmd+Shift+R, Windows Ctrl+Shift+R.';
  updateBanner.appendChild(hint);
  document.body.appendChild(updateBanner);
}

// Client-side version check: fetch version-info.json with cache: 'reload' so we get the latest
// even when the service worker would otherwise serve cached content. Works for all users (no hard refresh needed).
async function checkVersionFromServer(
  registration: ServiceWorkerRegistration,
  currentBuildId: string | null
): Promise<string | null> {
  try {
    const res = await fetch('/version-info.json', { cache: 'reload' });
    if (!res.ok) return currentBuildId;
    const data = await res.json();
    const serverBuildId = String(data.buildTimestamp ?? data.buildDate ?? data.version ?? '');
    if (serverBuildId && serverBuildId !== currentBuildId) return serverBuildId;
  } catch {
    // ignore
  }
  return currentBuildId;
}

// Register service worker for PWA functionality (only in production)
if ('serviceWorker' in navigator) {
  if (process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then(async (registration) => {
          console.log('SW registered: ', registration);

          // Client-side version check: works even when SW would serve cached sw.js.
          // Fetch with cache: 'reload' so we get latest; request is passed through by SW.
          let currentBuildId: string | null = null;
          const setBuildId = async () => {
            const fromServer = await checkVersionFromServer(registration, currentBuildId);
            if (fromServer !== currentBuildId) {
              if (currentBuildId != null) showUpdateBanner(registration);
              currentBuildId = fromServer ?? currentBuildId;
            }
          };
          await setBuildId();
          setInterval(setBuildId, 60000);

          registration.update();
          setInterval(() => registration.update(), 60000);

          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  showUpdateBanner(registration);
                }
              });
            }
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  } else {
    // Unregister service worker in development to prevent caching issues
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
          console.log('SW unregistered for development');
        }
      });
    });
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
